<template>
  <div class="daily-routine-viewer" v-if="showDailyRoutineViewer" :class="{ 'closing': isClosing, 'opening-animation': isOpeningAnimation }">
    <div class="page-wrapper" :style="{ 'height': '100dvh', 'height': '-webkit-fill-available' }">
      <div class="content">
        <div class="close-action" v-on:click="close">
          <img :src="require(`@/assets/icons/close-white.svg`)"/>
        </div>
        <div class="page-content" ref="pageContent">
          <div class="dr-container">
            <div v-for="(dailyRoutine, index) in dailyRoutinesToDisplay"
                 :key="dailyRoutine.entityId || dailyRoutine.type"
                 :id="dailyRoutine.entityId || dailyRoutine.type"
                 :ref="el => cardRefs[dailyRoutine.entityId || dailyRoutine.type] = el"
                 :style="cardScaleCss(index)"
                 class="daily-routine-item"
                 @touchstart="startDrag($event, dailyRoutine.entityId || dailyRoutine.type)"
                 @mousedown="startDrag($event, dailyRoutine.entityId || dailyRoutine.type)">
              <div class="background" :style="{ background: dailyRoutine.page.content.background }"></div>
              <viewer-widgets v-if="dailyRoutine.page.content.widgets" :widgets="dailyRoutine.page.content.widgets"
                              @userInteraction="onUserInteraction" />
            </div>
          </div>
        </div>
        <div v-if=hasCardEditActions class="buttons flex-row">
          <riseup-button
              :title="dismissAction.text"
              :action="($event) => handleAction(dismissAction.action, $event)"
              variant="secondary"
              :style="dismissActionStyle"
              class="action-button"/>
          <div class="card-text">נשארו עוד {{dailyRoutines.length}}</div>
          <riseup-button
              v-if="editAction"
              :title="editAction.text"
              variant="secondary"
              :action="($event) => handleAction(editAction.action, $event)"
              :style="editActionStyle"
              class="action-button"/>
        </div>
        <div v-else class="buttons flex-row">
          <div class="card-text" @click="handleNoEditAction($event)">{{ noneAction.text }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import storyLib from '@riseupil/story-lib';
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import BaseUI from '@riseupil/base-ui';
import ChangeTransactionCategoryPopup
  // eslint-disable-next-line max-len
  from '@/pages/responsive-pages/authenticated/navigation-pages/cashflow-app/cashflow-view/current-month/components/cashflow-edit/edit-transaction/ChangeTransactionCategoryPopup.vue';
import EditAmountPopup
  // eslint-disable-next-line max-len
  from '@/pages/responsive-pages/authenticated/navigation-pages/cashflow-app/cashflow-view/current-month/components/cashflow-edit/EditAmountPopup.vue';
import consts from '@riseupil/story-lib/src/components/consts';
import Intercom from '@/Intercom';
import Segment from '@/Segment';
import EventBus from '@/event-bus/event-bus';

export default {
  name: 'DailyRoutineViewer',
  components: {
    ViewerWidgets: storyLib.ViewerWidgets,
    RiseupButton: BaseUI.RiseupButton,
  },
  data() {
    return {
      containerHeight: 0,
      containerWidth: 0,
      startX: 0,
      startY: 0,
      currentX: 0,
      currentY: 0,
      isDragging: false,
      currentDraggedElement: null,
      cardRefs: {},
      initialTransform: null,
      swipeDirection: null,
      isClosing: false,
      isOpeningAnimation: false,
    };
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize);
    EventBus.$off('transaction-menu-closed', this.onActionDone);
  },
  watch: {
    showDailyRoutineViewer(val) {
      if (val) {
        this.$nextTick(() => {
          Segment.trackUserInteraction('DailyRoutine__Open', { numberOfCards: this.dailyRoutines.length });
          this.isOpeningAnimation = true;
          Intercom.hideLauncher();
          Intercom.hide();
          this.onResize();
          window.addEventListener('resize', this.onResize);
          EventBus.$on('transaction-menu-closed', this.onActionDone);
        });
      } else {
        window.removeEventListener('resize', this.onResize);
      }
    },
  },
  computed: {
    ...mapState('dailyRoutines', ['dailyRoutines', 'showDailyRoutineViewer', 'specialCards', 'isIntroDismissed']),
    ...mapState('cashflowView', ['presentedBudgetDate']),
    ...mapGetters('cashflowView', ['trackingCategories', 'currentWeek']),
    ...mapGetters('dailyRoutines', ['doneCard']),
    trackingCategory() {
      return this.trackingCategories.find(category => category.categoryId === this.trackingCategoryId);
    },
    trackingCategoryId() {
      return this.transaction ? this.transaction?.categoryId : this.selectedDailyRoutine.data?.id;
    },
    transaction() {
      return this.selectedDailyRoutine.transaction;
    },
    dismissAction() {
      return this.selectedDailyRoutine.actions.find(a => a.action === 'dismiss');
    },
    noneAction() {
      return this.selectedDailyRoutine.actions.find(a => a.action === 'none');
    },
    hasCardEditActions() {
      return this.dismissAction && this.editAction;
    },
    editAction() {
      return this.selectedDailyRoutine.actions.length > 1 ? this.selectedDailyRoutine.actions.find(a => a.action !== 'dismiss') : null;
    },
    dailyRoutinesToDisplay() {
      return [
        ...(this.isIntroDismissed ? [] : [this.specialCards.INTRO]),
        ...this.dailyRoutinesIncludingDoneCard,
      ].slice(0, 4);
    },
    dailyRoutinesIncludingDoneCard() {
      return [...this.dailyRoutines, this.doneCard].slice(0, 4);
    },
    cardScale() {
      const cardHeightRatio = this.containerHeight / consts.BASE_SCREEN_HEIGHT;
      const cardWidthRatio = this.containerWidth / consts.BASE_SCREEN_WIDTH;
      return Math.min(cardHeightRatio, cardWidthRatio);
    },
    selectedDailyRoutine() {
      return this.dailyRoutinesToDisplay[0];
    },
    dismissActionStyle() {
      if (!this.isDragging) {
        return {};
      }
      return this.swipeDirection === 'right'
        ? { background: BaseUI.Colors.riseupGreen, borderColor: BaseUI.Colors.riseupGreen }
        : {};
    },
    editActionStyle() {
      if (!this.isDragging) {
        return {};
      }
      return this.swipeDirection === 'left'
        ? { background: BaseUI.Colors.riseupDangerRed, borderColor: BaseUI.Colors.riseupDangerRed }
        : {};
    },
    swipeThreshold() {
      return 50;
    },
  },
  methods: {
    ...mapActions('dailyRoutines', ['dismissDailyRoutine', 'dismissIntro', 'markTasksCompletedForToday', 'fetchDailyRoutinesNextChunk']),
    ...mapMutations('dailyRoutines', ['setShowDailyRoutineViewer']),
    ...mapActions('modalRootStore', ['openModal']),
    ...mapActions('editTransaction', ['startEditingTransaction']),
    onResize() {
      // const styles = window.getComputedStyle(this.$refs.pageContent);
      // Calculate content height (without padding)
      this.containerHeight = this.$refs.pageContent.clientHeight - 44;

      // Calculate content width (without padding)
      this.containerWidth = this.$refs.pageContent.clientWidth;
    },

    getButtonToActionPopupMap() {
      const openTransactionBottomMenu = async () => {
        setImmediate(() => EventBus.$emit('open-transaction', { transactionId: this.transaction.transactionId }));
      };

      // eslint-disable-next-line no-unused-vars
      const openChangeCategoryModal = async () => {
        await this.startEditingTransaction({
          transaction: this.transaction,
          categoryName: this.transaction.categoryName,
          categoryId: this.transaction.categoryId,
        });
        await this.openModal({ component: ChangeTransactionCategoryPopup });
      };
      const openSetCategoryModal = async () => {
        await this.openModal({
          component: EditAmountPopup,
          props: {
            expenseCategory: this.trackingCategory.categoryName,
            category: this.trackingCategory?.categories.length > 1
              ? this.trackingCategory.categories[this.currentWeek.weekIndex]
              : this.trackingCategory.categories[0],
          },
          eventHandlers: {
            close: this.onActionDone,
          },
        });
      };
      const DAILY_ROUTINE_ACTION_TYPES = {
        TRANSACTION_MENU: 'transaction_menu',
        CATEGORY_AMOUNT_SET: 'category_amount_set',
      };
      return {
        [DAILY_ROUTINE_ACTION_TYPES.TRANSACTION_MENU]: openTransactionBottomMenu,
        [DAILY_ROUTINE_ACTION_TYPES.CATEGORY_AMOUNT_SET]: openSetCategoryModal,
      };
    },

    close() {
      Segment.trackUserInteraction('DailyRoutine__Close', { numberOfCards: this.dailyRoutines.length });
      this.isClosing = true;
      setTimeout(() => {
        if (this.dailyRoutines.length === 0) {
          this.markTasksCompletedForToday();
        }
        Intercom.showLauncher();
        this.setShowDailyRoutineViewer(false);
        this.isClosing = false;
      }, 300);
    },

    async onActionDone() {
      Segment.trackUserInteraction('DailyRoutine__PerformedActionDone', { dailyRoutine: this.selectedDailyRoutine.type });
      Intercom.hideLauncher();
      Intercom.hide();
      await this.dismissDailyRoutine(this.selectedDailyRoutine.entityId);
    },

    async handleAction(type, e) {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      this.isOpeningAnimation = false;
      Segment.trackUserInteraction('DailyRoutine__ActionButtonClicked', { type, dailyRoutine: this.selectedDailyRoutine.type });
      if (type === 'dismiss') {
        await this.dismissCardAction();
      } else {
        await this.executeCardAction();
      }
    },

    async executeCardAction() {
      switch (this.selectedDailyRoutine.type) {
      case 'INTRO':
        await this.dismissIntro();
        break;
      case 'DONE':
        await this.close();
        break;
      case 'DONE_WITH_MORE_CARDS':
        await this.close();
        break;
      default:
        await this.showActionPopup();
      }
    },

    async dismissCardAction() {
      switch (this.selectedDailyRoutine.type) {
      case 'INTRO':
        await this.dismissIntro();
        break;
      case 'DONE':
        await this.close();
        break;
      case 'DONE_WITH_MORE_CARDS':
        await this.close();
        break;
      default:
        await this.dismissDailyRoutine(this.selectedDailyRoutine.entityId);
      }
    },

    async handleNoEditAction(e) {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      switch (this.selectedDailyRoutine.type) {
      case 'INTRO':
        await this.dismissIntro();
        break;
      case 'DONE':
        await this.markTasksCompletedForToday();
        await this.close();
        break;
      case 'DONE_WITH_MORE_CARDS':
        await this.fetchDailyRoutinesNextChunk();
        break;
      default:
        await this.dismissCardAction();
      }
    },

    async showActionPopup() {
      const mapper = this.getButtonToActionPopupMap();
      if (mapper[this.editAction?.action]) {
        await mapper[this.editAction?.action]();
      } else {
        console.log('no mapper for action', `${this.editAction?.action}`);
      }
    },

    cardScaleCss(index) {
      const cardScaleRatio = index === 0 ? 1 : 0.9 ** index;

      let top = 0;
      if (index === 1) {
        top = 16;
      } else if (index === 2) {
        top = 28;
      } else if (index === 3) {
        top = 36;
      }
      const translateX = (this.containerWidth - consts.BASE_SCREEN_WIDTH) / 2;
      const translateY = (((this.containerHeight - consts.BASE_SCREEN_HEIGHT) / cardScaleRatio) / 2);
      const dx = this.currentX - this.startX;
      const rotation = this.isDragging ? dx / 10 : '';
      return {
        transform: `scale(${cardScaleRatio * this.cardScale}) translate(${translateX}px, ${translateY - top}px ${rotation})`,
        zIndex: 4 - index,
      };
    },

    animatingCardScaleTransform(index) {
      const cardScaleRatio = index === 1 ? 1 : 0.9 ** (index - 1);

      let top = 0;
      if (index === 2) {
        top = 16;
      } else if (index === 3) {
        top = 28;
      }
      const translateX = (this.containerWidth - consts.BASE_SCREEN_WIDTH) / 2;
      const translateY = (((this.containerHeight - consts.BASE_SCREEN_HEIGHT) / cardScaleRatio) / 2);
      const dx = this.currentX - this.startX;
      const rotation = this.isDragging && index === 0 ? dx / 10 : '';
      return `scale(${cardScaleRatio * this.cardScale}) translate(${translateX}px, ${translateY - top}px ${rotation})`;
    },

    startDrag(e, entityId) {
      // Check if the clicked element is a button-widget or a child of button-widget
      // because we want to allow the click within the button and not stop the event cascade
      const clickedElement = e.target;
      const isButtonWidget = clickedElement.classList.contains('button-widget');
      const isChildOfButtonWidget = clickedElement.parentElement?.classList.contains('button-widget');
      if (isButtonWidget || isChildOfButtonWidget) {
        return;
      }

      if (this.isDragging) {
        return;
      }

      if (e) {
        e.stopPropagation();
        e.preventDefault();
      }

      this.isDragging = true;
      this.isOpeningAnimation = false;

      // Save the initial transform to restore it later
      this.currentDraggedElement = this.cardRefs[entityId];
      const style = window.getComputedStyle(this.currentDraggedElement);
      this.initialTransform = style.transform;

      // Handle both mouse and touch events
      const clientX = e.clientX || (e.touches && e.touches[0] ? e.touches[0].clientX : 0);
      const clientY = e.clientY || (e.touches && e.touches[0] ? e.touches[0].clientY : 0);

      this.startX = clientX;
      this.startY = clientY;
      this.currentX = clientX;
      this.currentY = clientY;

      document.addEventListener('mousemove', this.onDrag, { passive: false });
      document.addEventListener('mouseup', this.endDrag);
      document.addEventListener('touchmove', this.onDrag, { passive: false });
      document.addEventListener('touchend', this.endDrag);
    },

    onDrag(e) {
      if (!this.isDragging) {
        return;
      }

      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }

      // Handle both mouse and touch events
      this.currentX = e.clientX || (e.touches && e.touches[0] ? e.touches[0].clientX : 0);

      // Calculate the drag distance
      const dx = this.currentX - this.startX;

      // Apply the transform directly to the card element
      const dampedDx = dx * 1.2;
      const rotate = dampedDx / 40;

      if (Math.abs(dx) > this.swipeThreshold) {
        this.swipeDirection = dx > 0 ? 'right' : 'left';
      } else {
        this.swipeDirection = null;
      }

      this.currentDraggedElement.style.transform = `${this.initialTransform} translate3d(${dampedDx}px, 0, 0) rotate(${rotate}deg)`;
    },

    endDrag(e) {
      if (!this.isDragging) {
        return;
      }

      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      if (this.swipeDirection) {
        this.animateSwipe();
        this.removeEventListeners();
      } else {
        this.resetCardPosition();
        this.resetDragState();
        this.removeEventListeners();
      }
    },

    resetCardPosition() {
      if (this.currentDraggedElement && this.initialTransform) {
        this.currentDraggedElement.style.transition = 'transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)';
        this.currentDraggedElement.style.transform = this.initialTransform;
      }
    },

    animateSwipe() {
      const el = this.currentDraggedElement;
      el.classList.add('fading-out');
      const nextCard = this.dailyRoutines[1];
      const nextElement = this.cardRefs[nextCard?.entityId || nextCard?.type];
      const nextCard2 = this.dailyRoutines[2];
      const nextElement2 = this.cardRefs[nextCard2?.entityId || nextCard?.type];
      const nextCard3 = this.dailyRoutines[3];
      const nextElement3 = this.cardRefs[nextCard3?.entityId || nextCard?.type];

      if (nextElement) {
        nextElement.style.transform = this.animatingCardScaleTransform(1);
        nextElement.style.transition = 'transform 0.2s ease-in-out';
      }
      if (nextElement2) {
        nextElement2.style.transform = this.animatingCardScaleTransform(2);
        nextElement2.style.transition = 'transform 0.2s ease-in-out';
      }
      if (nextElement3) {
        nextElement3.style.transform = this.animatingCardScaleTransform(3);
        nextElement3.style.transition = 'transform 0.2s ease-in-out';
      }

      setTimeout(async () => {
        Segment.trackUserInteraction('DailyRoutine__SwipeCard', { direction: this.swipeDirection, dailyRoutine: this.selectedDailyRoutine.type });
        if (this.swipeDirection === 'left') {
          await this.executeCardAction();
        } else {
          await this.dismissCardAction();
        }
        this.resetDragState();
        if (nextElement) {
          nextElement.style.transform = this.cardScaleCss(0).transform;
          nextElement.style.transition = 'none';
        }
        if (nextElement2) {
          nextElement2.style.transform = this.cardScaleCss(1).transform;
          nextElement2.style.transition = 'none';
        }
        if (nextElement3) {
          nextElement3.style.transform = this.cardScaleCss(2).transform;
          nextElement3.style.transition = 'none';
        }
      }, 200);
    },

    resetDragState() {
      this.isDragging = false;
      this.currentX = 0;
      this.currentY = 0;
      this.startX = 0;
      this.startY = 0;
      this.swipeDirection = null;
    },

    removeEventListeners() {
      document.removeEventListener('mousemove', this.onDrag);
      document.removeEventListener('mouseup', this.endDrag);
      document.removeEventListener('touchmove', this.onDrag);
      document.removeEventListener('touchend', this.endDrag);
    },

    onUserInteraction(data) {
      if (data.eventName === 'fetchMoreCards') {
        Segment.trackUserInteraction('DailyRoutine__fetchMoreCards');
        this.isOpeningAnimation = true;
        this.fetchDailyRoutinesNextChunk();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@riseupil/base-ui/src/scss/riseup-colors';

.daily-routine-viewer {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: absolute;
  width: 100%;
  height: 100%;
  opacity: 0;
  animation: fadeIn 0.3s ease-in-out forwards;
  &.closing {
    animation: fadeOut 0.3s ease-in-out forwards;
  }

  &.opening-animation {
    .daily-routine-item {
      opacity: 0;
      // First card (index 0) appears immediately
      &:nth-child(1) {
        animation: fadeIn 0.3s ease-in-out forwards;
      }

      // All other cards appear after 0.3s delay
      &:nth-child(n+2) {
        animation: fadeIn 0.3s ease-in-out 0.3s forwards;
      }
    }
  }

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @keyframes fadeOut {
    from { opacity: 1; }
    to { opacity: 0; }
  }

  .page-wrapper {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100dvh;
    overscroll-behavior: none;
  }

  @media (min-width: 430px) {
    .page-wrapper {
      width: 400px;
    }
  }

  .content {
    position: relative;
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    overflow-y: scroll;
    overflow-x: hidden;
    background: #000000b3;
    overscroll-behavior: none;
    touch-action: none;

    .close-action {
      z-index: 10;
      margin: 20px 16px 28px;
      cursor: pointer;
    }

    .buttons {
      align-items: center;
      justify-content: space-between;
      margin: 20px 16px 20px;

      .action-button {
        color: $riseup_white;
        width: 100px;
        border: 1px solid $riseup_white;
      }

      .card-text {
        display: flex;
        align-items: center;
        flex: 1;
        justify-content: center;
        color: $riseup_white;
        height: 54px;
      }
    }

    .page-content {
      background: transparent;
      margin: 0 16px;
      padding-top: 90px; // This is done for the 4 cards in stack
      flex-grow: 1;
      position: relative;
    }
  }

  .dr-container {
    position: relative;
    overscroll-behavior-x: contain;
    overscroll-behavior-y: contain;
    overscroll-behavior: none;
    touch-action: none;
    user-select: none;

    .daily-routine-item {
      position: relative;
      cursor: grab;

      &.fading-out {
        animation: fadeOut 0.2s ease-in-out forwards;
      }

      &:active {
        cursor: grabbing;
      }

      .background {
        position: absolute;
        top: 0;
        left: 0;
        height: 768px;
        width: 430px;
        border-radius: 24px;
        box-shadow: 0px -4px 11.6px 0px #48233021;
      }
      height: 100%;
    }
  }

}
</style>
